import * as React from 'react'

import { Button } from '@ui/components/ui/button'
import { cn } from '@ui/lib/utils'

export interface PaginationProps extends React.ComponentProps<'nav'> {
  /** Aktuelle Seite, einsbasiert. */
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Grammatisch passende Mehrzahl, zum Beispiel „Vorgängen“. */
  itemLabel?: string
  'aria-label'?: string
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  itemLabel,
  className,
  'aria-label': ariaLabel = 'Seitennavigation',
  ...props
}: PaginationProps) {
  const safeTotal = Number.isFinite(total) ? Math.max(0, Math.trunc(total)) : 0
  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0 ? Math.max(1, Math.trunc(pageSize)) : 1
  const lastPage = Math.max(1, Math.ceil(safeTotal / safePageSize))
  const normalizedPage = Number.isFinite(page) ? Math.trunc(page) || 1 : 1
  const currentPage = Math.min(lastPage, Math.max(1, normalizedPage))
  const first = safeTotal <= 0 ? 0 : (currentPage - 1) * safePageSize + 1
  const last = Math.min(currentPage * safePageSize, safeTotal)

  if (safeTotal <= 0) return null
  if (safeTotal <= safePageSize) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)} data-numeric>
        Seite {currentPage} von {lastPage}
      </p>
    )
  }

  const itemSuffix = itemLabel ? ` ${itemLabel}` : ''

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        'flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
      {...props}
    >
      <p className="hidden text-sm text-muted-foreground sm:block" data-numeric>
        {first}–{last} von {safeTotal}
        {itemSuffix}
      </p>
      <div className="flex min-w-0 items-center justify-between gap-2 sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Zurück
        </Button>
        <span
          className="hidden shrink-0 text-sm text-muted-foreground sm:inline"
          data-numeric
        >
          Seite {currentPage} von {lastPage}
        </span>
        <span className="shrink-0 text-sm text-muted-foreground sm:hidden" data-numeric>
          {currentPage}/{lastPage}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={currentPage >= lastPage}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Weiter
        </Button>
      </div>
    </nav>
  )
}
